import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { GenericField, OId } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { capitalizeStr, getDefaultDateFormat } from '@Helpers';
import { useSessionStore } from '@Stores';
import { ROUTES } from '@AnimalModule';
import { useForm } from '@FormModule';
import { MedicalStaff } from '@ShelterModule';

interface MedicalStaff {
  [key: string]: any;
  first_name: string;
  last_name: string;
  _id: OId;
}

const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const formError = ref();
const medicalStaff = ref();
const isActive = ref();

export default function () {
  const { t } = useI18n();
  const { getDetailsSchema, wasFormSubmitted } = useForm();
  const { shelterProfile } = storeToRefs(useSessionStore());

  const rawFields: RawField[] = [
    {
      label: 'Reason for Treatment',
      name: 'reason',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Start Date',
      name: 'start_date',
      required: true,
      type: 'date',
      value: '',
    },
    {
      label: 'Start Time',
      name: 'time',
      required: false,
      type: 'date',
      value: '',
    },
    {
      label: 'Route',
      name: 'route',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Amount',
      name: 'amount',
      required: false,
      type: 'amount_unit',
      value: '',
    },
    {
      label: '',
      name: 'multi_dose',
      required: false,
      type: 'toggle',
      value: false,
    },
    {
      label: '',
      name: 'separator_frequency',
      required: false,
      type: 'seperator',
      value: '',
    },
    {
      label: 'Frequency',
      name: 'frequency',
      required: false,
      type: 'frequency',
      value: '',
    },
    {
      label: 'Was the initial dose completed?',
      name: 'complete_first_dose',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: '',
      name: 'separator_frequency',
      required: false,
      type: 'seperator',
      value: '',
    },
    {
      label: 'Notes',
      name: 'notes',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Attending Vet',
      name: 'attending_vet',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Other Veterinarian',
      name: 'other_by',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Set Default Costs',
      name: 'track_medical_costs',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: 'Clinic Cost',
      name: 'clinic_cost',
      required: false,
      type: 'cost_unit',
      value: '',
    },
    {
      label: 'Retail Cost',
      name: 'retail_cost',
      required: false,
      type: 'cost_unit',
      value: '',
    },
  ];

  const attendingVetOptions = computed(() =>
    medicalStaff.value.map((staff: MedicalStaff) => {
      const { first_name, last_name } = staff;
      return {
        ...staff,
        by: `${first_name} ${last_name}`,
      };
    })
  );

  const fieldsSettings = computed(
    (): FieldInitialSettings => ({
      status: {
        settings: {
          hint: 'animals.individualPage.medical.medications.completeMedicationHint',
        },
      },
      attending_vet: {
        edit: {
          label: 'by',
          options: [...attendingVetOptions.value, { by: t('common.other') }],
        },
        settings: {
          required: false,
          show: true,
        },
      },
      route: {
        edit: {
          options: Object.values(ROUTES),
        },
      },
      start_date: {
        settings: {
          disabled: !isActive.value,
        },
      },
      frequency: {
        settings: {
          required: false,
          show: false,
        },
      },
      complete_first_dose: {
        state: {
          disabled: true,
          errors: new Set(),
        },
      },
      separator_frequency: {
        settings: {
          show: false,
        },
      },
      followup_notes: {
        settings: {
          required: false,
          show: false,
        },
      },
      time: {
        settings: {
          timeOnly: true,
        },
      },
      separator_followup: {
        settings: {
          show: false,
        },
      },
      time_until_followup: {
        settings: {
          hasCalendar: true,
          required: false,
          show: false,
        },
      },
      multi_dose: {
        model: false,
        settings: {
          label: 'Multiple Doses / Treatments',
        },
      },
      schedule_followup: {
        settings: {
          label: 'Schedule a Follow-Up Check',
        },
      },
      track_medical_costs: {
        settings: {
          show: !!shelterProfile.value?.track_medical_costs,
        },
      },
      clinic_cost: {
        settings: {
          required: false,
          show: false,
          allowZeroAsValidModel: true,
          reverseCalculations: true,
        },
      },
      retail_cost: {
        settings: {
          required: false,
          show: false,
          allowZeroAsValidModel: true,
          reverseCalculations: true,
        },
      },
    })
  );

  const multiDoseField = computed(() => fieldsSchema.value.find(({ name }) => name === 'multi_dose'));
  const followUpField = computed(() => fieldsSchema.value.find(({ name }) => name === 'schedule_followup'));
  const byField = computed(() => fieldsSchema.value.find(({ name }) => name === 'attending_vet'));
  const amountField = computed(() => fieldsSchema.value.find(({ name }) => name === 'amount'));
  const frequencyField = computed(() => fieldsSchema.value.find(({ name }) => name === 'frequency'));
  const frequencyModel = computed(() => frequencyField.value?.model);
  const amountModel = computed(() => amountField.value?.model);
  const byModel = computed(() => byField.value?.model);
  const isMultiDose = computed(() => multiDoseField.value?.model);
  const isFollowUp = computed(() => followUpField.value?.model);
  const trackMedicalCostsField = computed(() => fieldsSchema.value.find(({ name }) => name === 'track_medical_costs'));
  const trackMedicalCostsModel = computed(() => trackMedicalCostsField.value?.model);

  onMounted(() => fillMedicalStaff());

  watch(isMultiDose, () => showFrequencyFields());
  watch(isFollowUp, () => showFollowUpFields());
  watch(byModel, () => showOtherVetField());
  watch(isActive, (newVal) => updateActiveStartDate(newVal));
  watch(trackMedicalCostsModel, () => showMedicalCostsFields());
  watch([amountModel, frequencyModel], () => passAmountToMedicalCostFields());

  function passAmountToMedicalCostFields() {
    const totalDoses = frequencyModel?.value?.doses || 1;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      field.settings.amount = {
        amount: amountModel?.value?.amount,
        amount_unit: amountModel?.value?.amount_unit,
        doses: totalDoses,
      };
    });
  }

  function updateActiveStartDate(newVal: any) {
    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'start_date') return;

      field.settings = {
        ...field.settings,
        disabled: !newVal,
      };
    });
  }

  function showMedicalCostsFields() {
    if (!shelterProfile.value?.track_medical_costs) return;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      field.settings = {
        ...field.settings,
        show: trackMedicalCostsModel.value,
        required: trackMedicalCostsModel.value,
      };
      field.state?.errors?.clear();
    });
  }

  function showOtherVetField() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'other_by') {
        const showField = byModel?.value?.by === t('common.other');

        field.settings = {
          ...field.settings,
          show: showField,
          required: showField,
        };

        if (byModel?.value?.by !== t('common.other')) {
          field.model = '';
          field.value = '';
          field.state.errors.clear();
        }
      }
    });

    return true;
  }

  function showFrequencyFields() {
    fieldsSchema.value.forEach((field) => {
      const frequencyFields = ['frequency', 'separator_frequency', 'time'];

      if (frequencyFields.includes(field.name)) {
        const showField = isMultiDose.value;

        field.settings = {
          ...field.settings,
          required: field.name === 'frequency' ? showField : false,
          show: field.name === 'time' ? !showField : showField,
        };

        field.model = undefined;

        if (!isMultiDose.value) field.state.errors.clear();
      }
    });

    return true;
  }

  function showFollowUpFields() {
    fieldsSchema.value.forEach((field) => {
      const followUpFields = ['followup_notes', 'followup_reason', 'separator_followup', 'time_until_followup'];

      if (followUpFields.includes(field.name)) {
        const showField = isFollowUp.value === true;

        field.settings = {
          ...field.settings,
          required: field.name === 'separator_followup' ? false : showField,
          show: showField,
        };

        if (!showField) field.state.errors.clear();
      }
    });

    return true;
  }

  function setForm() {
    if (fieldsSchema.value.length) return;

    fieldsSchema.value = getDetailsSchema(rawFields, fieldsSettings.value);
  }

  function updateSchemaModels(sourceData: any) {
    setForm();

    formError.value = null;
    wasFormSubmitted.value = false;

    const toggleableFields = [
      'frequency',
      'separator_frequency',
      'followup_notes',
      'followup_reason',
      'time_until_followup',
      'clinic_cost',
      'retail_cost',
    ];

    fieldsSchema.value.forEach((field) => {
      const match = sourceData[field.name];
      if (toggleableFields.includes(field.name)) return;

      switch (field.name) {
        case 'status': {
          setMatchStatusField(field, sourceData);
          break;
        }
        case 'complete_first_dose': {
          setMatchCompletedFirstDoseField(field, sourceData);
          break;
        }
        case 'reason': {
          setMatchField(field, match);
          break;
        }
        case 'attending_vet': {
          setByField(field, sourceData);
          break;
        }
        case 'other_by': {
          setOtherByField(field, sourceData);
          break;
        }
        case 'route': {
          setMatchField(field, match);
          break;
        }
        case 'notes': {
          setMatchField(field, match);
          break;
        }
        case 'start_date': {
          setDateField(field, sourceData);
          break;
        }
        case 'amount': {
          setAmountField(field, sourceData);
          break;
        }
        case 'track_medical_costs': {
          setMatchField(field, match || false);
          showMedicalCostsFields();
          nextTick(() => {
            fillMedicalCostsFields(sourceData);
          });
          break;
        }
        case 'multi_dose': {
          setMatchField(field, match);
          showFrequencyFields();
          nextTick(() => {
            fillFrequencyFields(sourceData);
          });
          break;
        }
        case 'schedule_followup': {
          setMatchField(field, match);
          showFollowUpFields();
          nextTick(() => {
            fillFollowUpFields(sourceData);
          });
          break;
        }
      }
    });
  }

  function setMatchField(field: any, match: any) {
    field.model = match;
    field.value = match;
    field.state.errors.clear();
  }

  function fillMedicalCostsFields(sourceData: any) {
    if (!shelterProfile.value?.track_medical_costs) return;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      const costValue = sourceData.medical_costs_settings?.[field.name];
      const costType = sourceData.medical_costs_settings?.[`${field.name}_type`];

      if (costValue !== undefined && costValue !== null && costType) {
        field.model = {
          value: Number(costValue),
          type: costType,
        };
        field.value = {
          value: Number(costValue),
          type: costType,
        };
      }
      field.state.errors.clear();
    });
  }

  function setMatchCompletedFirstDoseField(field: any, sourceData: any) {
    if (!sourceData.schedule.length || !sourceData.schedule[0].status) return;

    field.model = sourceData.schedule[0].status === 'completed';
    field.value = sourceData.schedule[0].status === 'completed';
    field.state.errors.clear();
  }

  function setMatchStatusField(field: any, sourceData: any) {
    field.model = sourceData.status === 'active' ? 'Active' : 'Completed';
    field.value = sourceData.status === 'active' ? 'Active' : 'Completed';
    field.state.errors.clear();
  }

  function setDateField(field: any, sourceData: any) {
    field.model = getDefaultDateFormat(sourceData.start_date) || new Date();
    field.value = getDefaultDateFormat(sourceData.start_date) || new Date();
  }

  function setByField(field: any, sourceData: any) {
    if (!sourceData.attending_vet) return;

    if (sourceData.attending_vet_id === 'other' || !sourceData.attending_vet_id) {
      field.model = { by: 'Other' };
      field.value = { by: 'Other' };

      return;
    }

    const [first_name, last_name] = sourceData.attending_vet.split(' ');
    const by = {
      _id: sourceData.attending_vet_id,
      first_name,
      last_name,
      by: sourceData.attending_vet,
    };

    field.model = by;
    field.value = by;
  }

  function setOtherByField(field: any, sourceData: any) {
    if (!sourceData.attending_vet) return;

    field.model = sourceData.attending_vet;
    field.value = sourceData.attending_vet;
  }

  function setAmountField(field: any, sourceData: any) {
    field.model = { amount: sourceData.amount, amount_unit: sourceData.amount_unit };
    field.value = { amount: sourceData.amount, amount_unit: sourceData.amount_unit };
  }

  function setFrequencyField(field: any, sourceData: any) {
    const duration = sourceData.duration || '0';
    field.model = {
      frequency: sourceData.frequency,
      duration: duration,
      duration_unit: sourceData.duration_unit,
      time1: sourceData.time1 || new Date(),
      time2: sourceData.time2 || null,
      time3: sourceData.time3 || null,
      time4: sourceData.time4 || null,
      initial_dose_index: sourceData.initial_dose_index || 0,
    };
    field.value = {
      frequency: sourceData.frequency,
      duration: duration,
      duration_unit: capitalizeStr(sourceData.duration_unit),
      time1: sourceData.time1 || new Date(),
      time2: sourceData.time2 || null,
      time3: sourceData.time3 || null,
      time4: sourceData.time4 || null,
      initial_dose_index: sourceData.initial_dose_index || 0,
    };
  }

  function setTimeUntilFollowUpField(field: any, sourceData: any) {
    field.model = {
      duration: sourceData.time_until_followup,
      duration_unit: sourceData.followup_unit,
    };
    field.value = {
      duration: sourceData.time_until_followup,
      duration_unit: sourceData.followup_unit,
    };
  }

  function fillFrequencyFields(sourceData: any) {
    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'frequency') return;

      field.state.errors.clear();
      setFrequencyField(field, sourceData);
    });
  }

  function fillFollowUpFields(sourceData: any) {
    fieldsSchema.value.forEach((field) => {
      const followUpFields = ['followup_notes', 'followup_reason', 'time_until_followup'];
      const match = sourceData[field.name];

      if (!followUpFields.includes(field.name)) return;

      field.state.errors.clear();
      // prettier-ignore
      switch (field.name) {
        case 'followup_notes': return setMatchField(field, match);
        case 'followup_reason': return setMatchField(field, match);
        case 'time_until_followup': return setTimeUntilFollowUpField(field, sourceData);
      }
    });
  }

  function fillMedicalStaff() {
    MedicalStaff().then((result: any) => (medicalStaff.value = result));
  }

  function resetFieldSchemaEdit() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
    formError.value = null;
    wasFormSubmitted.value = false;
    isActive.value = false;
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    fieldsSettings,
    formError,
    isActive,
    resetFieldSchemaEdit,
    setForm,
    updateSchemaModels,
  };
}
